import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, Alert, CircularProgress } from '@mui/material';
import { AccountBalance as QuickBooksIcon, Link as LinkIcon, LinkOff as LinkOffIcon, CloudSync as CloudSyncIcon, PeopleAlt as PeopleIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { enhancedApi } from '../../api';
import SettingsPageLayout from './SettingsPageLayout';

const QuickBooksManagePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState({ connected: false, realm_id: null });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [autoSyncEnabled, setAutoSyncEnabled] = React.useState(false);
  const [firmReportMsg, setFirmReportMsg] = React.useState('');
  const [custSyncLoading, setCustSyncLoading] = useState(false);
  const [custSyncMsg, setCustSyncMsg] = useState('');
  const [unsyncedCount, setUnsyncedCount] = useState(null);

  const handleToggleAutoSync = async () => {
    try {
      const newVal = !autoSyncEnabled;
      await enhancedApi.toggleQBAutoSync(newVal);
      setAutoSyncEnabled(newVal);
    } catch (e) {}
  };

  const handleFirmReport = async (type) => {
    const year = new Date().getFullYear();
    try {
      await enhancedApi.getFirmQBReport(type, `${year}-01-01`, `${year}-12-31`);
      setFirmReportMsg(type === 'profit_loss' ? 'P&L report saved!' : 'Balance Sheet saved!');
    } catch (e) { setFirmReportMsg('Failed to pull report'); }
  };

  const handleFirmExpenses = async () => {
    const year = new Date().getFullYear();
    try {
      await enhancedApi.getFirmQBExpenses(`${year}-01-01`, `${year}-12-31`);
      setFirmReportMsg('Expenses report saved!');
    } catch (e) { setFirmReportMsg('Failed to pull expenses'); }
  };

  const fetchUnsyncedCount = async () => {
    try {
      const res = await enhancedApi.getQBUnsyncedCustomers();
      setUnsyncedCount(res.data?.unsynced_count ?? null);
    } catch {
      setUnsyncedCount(null);
    }
  };

  const handleSyncCustomers = async () => {
    setCustSyncLoading(true);
    setCustSyncMsg('');
    try {
      await enhancedApi.syncQBCustomers();
      setCustSyncMsg('Customer sync started. Refresh in a moment to see results.');
      setTimeout(fetchUnsyncedCount, 5000);
    } catch (err) {
      setCustSyncMsg(err.response?.data?.error || 'Failed to start customer sync');
    } finally {
      setCustSyncLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('quickbooks') === 'success') {
      setSuccessMessage('QuickBooks connected successfully!');
      window.history.replaceState({}, '', '/settings/quickbooks');
    }
  }, [searchParams]);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedApi.getQuickBooksStatus();
      setStatus(res.data);
      if (res.data?.connected) fetchUnsyncedCount();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load QuickBooks status');
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleConnect = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await enhancedApi.getQuickBooksConnectUrl();
      if (res.data?.auth_url) {
        window.location.href = res.data.auth_url;
        return;
      }
      setError(res.data?.error || 'Failed to get connection URL');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await enhancedApi.syncAllToQuickBooks();
      const d = res.data;
      const totalFailed = (d.clients_failed || 0) + (d.invoices_failed || 0);
      setSuccessMessage(
        `Synced ${d.clients_synced || 0} clients and ${d.invoices_synced || 0} invoices to QuickBooks` +
        (totalFailed > 0 ? `. ${totalFailed} failed.` : '.')
      );
      if (d.errors?.length) {
        setError(d.errors.slice(0, 3).join('; '));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setActionLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await enhancedApi.disconnectQuickBooks();
      setStatus({ connected: false, realm_id: null });
      setSuccessMessage('Connection cleared. You can now connect to the correct QuickBooks app.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <SettingsPageLayout
      title="QuickBooks Online"
      description="Sync clients, service items, invoices, payments, and credits"
      maxWidth={600}
    >
      <Button
        size="small"
        onClick={() => navigate('/settings/integrations')}
        sx={{ mb: 3, textTransform: 'none' }}
      >
        ← Back to Integrations
      </Button>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography>Checking connection...</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {status.connected ? (
                  <Box component="span" sx={{ color: 'success.main' }}>Connected</Box>
                ) : (
                  <Box component="span" sx={{ color: 'text.secondary' }}>Not connected</Box>
                )}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {!status.connected && (
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleConnect}
                  disabled={actionLoading}
                  color="success"
                >
                  Connect to QuickBooks
                </Button>
              )}
              {status.connected && (
                <Button
                  variant="contained"
                  startIcon={<CloudSyncIcon />}
                  onClick={handleSyncAll}
                  disabled={syncLoading}
                  color="success"
                >
                  {syncLoading ? 'Syncing...' : 'Sync all to QuickBooks'}
                </Button>
              )}
              <Button
                variant="outlined"
                color={status.connected ? 'error' : 'inherit'}
                startIcon={<LinkOffIcon />}
                onClick={handleDisconnect}
                disabled={actionLoading}
              >
                {status.connected ? 'Disconnect' : 'Clear connection'}
              </Button>
            </Box>

            {/* Auto-Sync Toggle */}
            {status.connected && (
              <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Invoice Auto-Sync
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Automatically push new invoices to QuickBooks when they are created.
                </Typography>
                <Button
                  variant={autoSyncEnabled ? 'contained' : 'outlined'}
                  color={autoSyncEnabled ? 'success' : 'primary'}
                  onClick={handleToggleAutoSync}
                  sx={{ mt: 1 }}
                >
                  {autoSyncEnabled ? 'Auto-Sync ON' : 'Auto-Sync OFF'}
                </Button>
              </Box>
            )}

            {/* Firm Financial Reports */}
            {status.connected && (
              <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Firm Financial Reports
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Pull your firm's own Profit & Loss and expenses from QuickBooks.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" onClick={() => handleFirmReport('profit_loss')}>
                    Profit & Loss (This Year)
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => handleFirmReport('balance_sheet')}>
                    Balance Sheet
                  </Button>
                  <Button variant="outlined" size="small" onClick={handleFirmExpenses}>
                    Expenses (This Year)
                  </Button>
                </Box>
                {firmReportMsg && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>{firmReportMsg}</Typography>
                )}
              </Box>
            )}

            {/* Customer Sync */}
            {status.connected && (
              <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Customer Sync
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Import customers from QuickBooks into Firmflow. Matches by email to avoid duplicates.
                </Typography>
                {unsyncedCount !== null && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {unsyncedCount > 0
                      ? `${unsyncedCount} new customer${unsyncedCount !== 1 ? 's' : ''} found in QuickBooks`
                      : 'All QuickBooks customers are synced'}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  startIcon={custSyncLoading ? <CircularProgress size={18} /> : <PeopleIcon />}
                  onClick={handleSyncCustomers}
                  disabled={custSyncLoading}
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  {custSyncLoading ? 'Syncing...' : 'Sync QB Customers'}
                </Button>
                {custSyncMsg && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {custSyncMsg}
                  </Typography>
                )}
              </Box>
            )}

            {!status.connected && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2" component="span">
                  Connected to the wrong app or account? Click <strong>Clear connection</strong> above, then sign out of the wrong Intuit account at{' '}
                  <a href="https://accounts.intuit.com" target="_blank" rel="noopener noreferrer">accounts.intuit.com</a> before connecting again.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </Paper>
    </SettingsPageLayout>
  );
};

export default QuickBooksManagePage;
